import { roleLabel } from "@/types";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, api } from "@/api/client";
import { PageHeader } from "@/components/PageHeader";
import { Skeleton } from "@/components/Skeleton";
import { Avatar } from "@/components/Avatar";
import { usePublishProgress } from "@/components/PublishProgress";
import { type Filters, FilterBar, filtersToBody } from "@/components/MarketShared";

// ── API shapes (mirror app/schemas/automation.py) ─────────────────────────
interface RuleOut {
  id: string;
  agent_user_id: string;
  enabled: boolean;
  filters: Record<string, unknown>;
  batch_size: number;
  weekdays: number[];
  run_times: string[];
  use_ss: boolean;
  mh_pay_balance: boolean;
  last_fired_at: string | null;
  created_at: string;
  updated_at: string;
}

interface RunOut {
  id: string;
  agent_user_id: string | null;
  trigger: string;
  fired_for: string | null;
  triggered_at: string;
  finished_at: string | null;
  status: string;
  skip_reason: string | null;
  scanned: number;
  matched: number;
  published: number;
  job_run_ids: string[];
  error: string | null;
}

interface SpawnedJob {
  job_id: string;
  job_run_id: string;
  hot_offer_id: string;
  agent_user_id: string;
  agent_name: string | null;
  title: string | null;
  source_url: string;
  use_ss: boolean;
}

interface RunNowOut {
  run: RunOut;
  jobs: SpawnedJob[];
}

interface RunJobStatus {
  job_run_id: string;
  status: string;
  source_url: string | null;
  finished_at: string | null;
  error_message: string | null;
  mh_url: string | null;
  ss_url: string | null;
}

interface AgentRow {
  agent_user_id: string;
  full_name: string | null;
  email: string;
  role: string;
  avatar_url: string | null;
  has_myhome_creds: boolean;
  has_myhome_phone: boolean;
  has_ss_creds: boolean;
  rule: RuleOut | null;
  last_run: RunOut | null;
}

// Mon=1 … Sun=7 (ISO), matching the backend + broker schedule.
const WEEKDAYS: { iso: number; label: string }[] = [
  { iso: 1, label: "Mon" },
  { iso: 2, label: "Tue" },
  { iso: 3, label: "Wed" },
  { iso: 4, label: "Thu" },
  { iso: 5, label: "Fri" },
  { iso: 6, label: "Sat" },
  { iso: 7, label: "Sun" },
];

// Editable per-agent draft, derived from a rule (or sensible defaults).
interface Draft {
  enabled: boolean;
  filters: Filters;
  batchSize: number;
  weekdays: number[];
  runTimes: string[];
  useSs: boolean;
  mhPayBalance: boolean;
}

function picksFromMinMax(
  min: unknown,
  max: unknown,
  cap: number,
): number[] | undefined {
  if (min == null) return undefined;
  const lo = Number(min);
  const hi = max == null ? cap : Math.min(Number(max), cap);
  const out: number[] = [];
  for (let i = lo; i <= hi; i++) out.push(i);
  if (max == null && !out.includes(cap)) out.push(cap); // "N+" open upper bound
  return out.length ? out : undefined;
}

/** Reverse of Market's ``filtersToBody`` — turns a stored MarketSearchIn body
 *  back into the editable ``Filters`` shape the FilterBar consumes. Room/bed/
 *  bath picks are reconstructed best-effort from their min/max bounds. */
function bodyToFilters(b: Record<string, unknown>): Filters {
  const f: Filters = {};
  if (b.deal_type) f.deal_type = b.deal_type as Filters["deal_type"];
  if (b.property_type)
    f.property_type = b.property_type as Filters["property_type"];
  if (b.platform) f.platform = b.platform as Filters["platform"];
  f.rooms_pick = picksFromMinMax(b.rooms_min, b.rooms_max, 5);
  f.bedrooms_pick = picksFromMinMax(b.bedrooms_min, b.bedrooms_max, 5);
  f.bathrooms_pick = picksFromMinMax(b.bathrooms_min, b.bathrooms_max, 4);
  // ``price_min`` / ``price_max`` + ``price_currency`` is what the
  // FilterBar emits now; ``price_min_usd`` / ``price_max_usd`` is what
  // rules saved before the currency toggle carry, and they are USD.
  if (b.price_min != null) f.price_min_usd = Number(b.price_min);
  else if (b.price_min_usd != null) f.price_min_usd = Number(b.price_min_usd);
  if (b.price_max != null) f.price_max_usd = Number(b.price_max);
  else if (b.price_max_usd != null) f.price_max_usd = Number(b.price_max_usd);
  if (b.price_currency === "GEL" || b.price_currency === "USD") {
    f.price_currency = b.price_currency;
  }
  if (b.area_min != null) f.area_min_sqm = Number(b.area_min);
  if (b.area_max != null) f.area_max_sqm = Number(b.area_max);
  if (b.floor_min != null) f.floor_min = Number(b.floor_min);
  if (b.floor_max != null) f.floor_max = Number(b.floor_max);
  if (b.total_floors_min != null)
    f.total_floors_min = Number(b.total_floors_min);
  if (b.total_floors_max != null)
    f.total_floors_max = Number(b.total_floors_max);
  if (b.year_built_min != null) f.year_built_min = Number(b.year_built_min);
  if (b.year_built_max != null) f.year_built_max = Number(b.year_built_max);
  if (Array.isArray(b.neighborhoods))
    f.neighborhoods = b.neighborhoods as string[];
  if (Array.isArray(b.streets)) f.streets = b.streets as string[];
  if (Array.isArray(b.condition)) f.condition = b.condition as string[];
  if (Array.isArray(b.building_status))
    f.building_status = b.building_status as string[];
  if (Array.isArray(b.project_type))
    f.project_type = b.project_type as string[];
  if (b.exclude_agency) f.exclude_agency = true;
  if (b.exclude_forbids_agents) f.exclude_no_agents = true;
  if (b.exclude_exclusive) f.exclude_exclusive = true;
  if (b.placed_by_us) f.placed_by_us = b.placed_by_us as Filters["placed_by_us"];
  return f;
}

function draftFromRule(rule: RuleOut | null): Draft {
  if (rule) {
    return {
      enabled: rule.enabled,
      filters: bodyToFilters(rule.filters || {}),
      batchSize: rule.batch_size,
      weekdays: [...rule.weekdays].sort((a, b) => a - b),
      runTimes: [...rule.run_times].sort(),
      useSs: rule.use_ss,
      mhPayBalance: rule.mh_pay_balance,
    };
  }
  return {
    enabled: true,
    filters: {},
    batchSize: 10,
    weekdays: [1, 2, 3, 4, 5],
    runTimes: ["09:00"],
    useSs: false,
    mhPayBalance: true,
  };
}

function fmtTime(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function runSummary(r: RunOut): string {
  if (r.status === "skipped")
    return `skipped (${r.skip_reason ?? "no reason"})`;
  if (r.status === "error") return `error: ${r.error ?? "unknown"}`;
  if (r.status === "running") return "running…";
  return `${r.published} published / ${r.matched} matched / ${r.scanned} scanned`;
}

export function AutoPublish() {
  const [rows, setRows] = useState<AgentRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [historyFor, setHistoryFor] = useState<AgentRow | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api<AgentRow[]>("/api/automation/rules");
      setRows(data);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Failed to load");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <PageHeader
        title="Agent automations"
        subtitle="Schedule unattended owner scan + publish per agent. At each window the agent's filters scan the market for owner-direct listings and the newest matches are published automatically."
      />
      {error && (
        <div className="mb-4 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">
          {error}
        </div>
      )}
      {rows === null ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-28 w-full rounded-xl" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500">No agents to configure.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <AgentCard
              key={row.agent_user_id}
              row={row}
              onSaved={load}
              onShowHistory={() => setHistoryFor(row)}
            />
          ))}
        </div>
      )}
      {historyFor && (
        <HistoryModal
          row={historyFor}
          onClose={() => setHistoryFor(null)}
        />
      )}
    </div>
  );
}

function AgentCard({
  row,
  onSaved,
  onShowHistory,
}: {
  row: AgentRow;
  onSaved: () => void;
  onShowHistory: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => draftFromRule(row.rule));
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [newTime, setNewTime] = useState("");
  const { register } = usePublishProgress();

  // Re-sync the draft if the row changes underneath us (e.g. after reload).
  useEffect(() => {
    setDraft(draftFromRule(row.rule));
  }, [row.rule]);

  const credsOk = row.has_myhome_creds && row.has_myhome_phone;
  const name = row.full_name || row.email;

  const patch = (p: Partial<Draft>) => setDraft((d) => ({ ...d, ...p }));

  const toggleWeekday = (iso: number) =>
    setDraft((d) => ({
      ...d,
      weekdays: d.weekdays.includes(iso)
        ? d.weekdays.filter((x) => x !== iso)
        : [...d.weekdays, iso].sort((a, b) => a - b),
    }));

  const addTime = () => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(newTime.trim());
    if (!m) {
      setMsg("Time must be HH:MM");
      return;
    }
    const h = Number(m[1]);
    const min = Number(m[2]);
    if (h > 23 || min > 59) {
      setMsg("Time out of range");
      return;
    }
    const norm = `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
    setDraft((d) => ({
      ...d,
      runTimes: d.runTimes.includes(norm)
        ? d.runTimes
        : [...d.runTimes, norm].sort(),
    }));
    setNewTime("");
    setMsg(null);
  };

  const removeTime = (t: string) =>
    setDraft((d) => ({ ...d, runTimes: d.runTimes.filter((x) => x !== t) }));

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await api(`/api/automation/rules/${row.agent_user_id}`, {
        method: "PUT",
        json: {
          enabled: draft.enabled,
          filters: filtersToBody(draft.filters),
          batch_size: draft.batchSize,
          weekdays: draft.weekdays,
          run_times: draft.runTimes,
          use_ss: draft.useSs,
          mh_pay_balance: draft.mhPayBalance,
        },
      });
      setMsg("Saved");
      onSaved();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    setMsg(null);
    try {
      const { run, jobs } = await api<RunNowOut>(
        `/api/automation/rules/${row.agent_user_id}/run-now`,
        { method: "POST" },
      );
      // Hand each spawned job to the shared PublishProgress panel so the
      // director watches the same live SSE rows a manual bulk publish
      // shows — instantly, instead of waiting on the 5s agency poller.
      for (const j of jobs) {
        register({
          jobId: j.job_id,
          jobRunId: j.job_run_id,
          origin: "bulk",
          title: j.title,
          sourceUrl: j.source_url,
          agentLabel: row.full_name || row.email || j.agent_name || null,
          agentAvatarUrl: row.avatar_url,
          useSs: j.use_ss,
        });
      }
      setMsg(
        jobs.length > 0
          ? `Started ${jobs.length} publish job${
              jobs.length === 1 ? "" : "s"
            } — watch the progress panel.`
          : `Run now: ${runSummary(run)}`,
      );
      onSaved();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : "Run failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/5 bg-slate-900/40 p-4">
      <div className="flex items-center gap-3">
        <Avatar url={row.avatar_url} name={name} email={row.email} size="md" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-100 truncate">{name}</span>
            <span className="text-xs text-slate-500">{roleLabel(row.role)}</span>
            {draft.enabled ? (
              <span className="rounded-full bg-emerald-500/15 text-emerald-300 text-[11px] px-2 py-0.5">
                on
              </span>
            ) : (
              <span className="rounded-full bg-slate-500/15 text-slate-400 text-[11px] px-2 py-0.5">
                off
              </span>
            )}
          </div>
          {row.last_run && (
            <div className="text-xs text-slate-500 mt-0.5">
              Last: {fmtTime(row.last_run.triggered_at)} —{" "}
              {runSummary(row.last_run)}
            </div>
          )}
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={(e) => patch({ enabled: e.target.checked })}
          />
          Enabled
        </label>
      </div>

      {!credsOk && (
        <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-300">
          This agent is missing MyHome credentials or phone — automations
          can't publish until those are set. Saving is disabled.
        </div>
      )}

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {/* Days */}
        <div>
          <div className="text-xs font-medium text-slate-400 mb-1.5">Days</div>
          <div className="flex flex-wrap gap-1">
            {WEEKDAYS.map((w) => {
              const on = draft.weekdays.includes(w.iso);
              return (
                <button
                  key={w.iso}
                  type="button"
                  onClick={() => toggleWeekday(w.iso)}
                  className={`px-2.5 py-1 rounded-md text-xs ${
                    on
                      ? "bg-sky-500/20 text-sky-200 border border-sky-500/40"
                      : "bg-slate-800/60 text-slate-400 border border-transparent"
                  }`}
                >
                  {w.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Batch size */}
        <div>
          <div className="text-xs font-medium text-slate-400 mb-1.5">
            Publish per window
          </div>
          <input
            type="number"
            min={1}
            max={100}
            value={draft.batchSize}
            onChange={(e) =>
              patch({ batchSize: Math.max(1, Number(e.target.value) || 1) })
            }
            className="w-24 rounded-md bg-slate-800/60 border border-white/10 px-2 py-1 text-sm text-slate-100"
          />
          <span className="ml-2 text-xs text-slate-500">
            newest owner matches
          </span>
        </div>
      </div>

      {/* Times */}
      <div className="mt-4">
        <div className="text-xs font-medium text-slate-400 mb-1.5">
          Times (Tbilisi)
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {draft.runTimes.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1 rounded-md bg-slate-800/60 border border-white/10 px-2 py-1 text-xs text-slate-200"
            >
              {t}
              <button
                type="button"
                onClick={() => removeTime(t)}
                className="text-slate-500 hover:text-rose-300"
                aria-label={`Remove ${t}`}
              >
                ×
              </button>
            </span>
          ))}
          <input
            type="time"
            value={newTime}
            onChange={(e) => setNewTime(e.target.value)}
            className="rounded-md bg-slate-800/60 border border-white/10 px-2 py-1 text-xs text-slate-100"
          />
          <button
            type="button"
            onClick={addTime}
            className="rounded-md bg-slate-700/60 px-2.5 py-1 text-xs text-slate-200 hover:bg-slate-700"
          >
            Add
          </button>
        </div>
      </div>

      {/* Options */}
      <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-300">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={draft.mhPayBalance}
            onChange={(e) => patch({ mhPayBalance: e.target.checked })}
          />
          Pay from MyHome balance
        </label>
        <label
          className={`flex items-center gap-2 ${
            row.has_ss_creds ? "" : "opacity-40"
          }`}
        >
          <input
            type="checkbox"
            checked={draft.useSs}
            disabled={!row.has_ss_creds}
            onChange={(e) => patch({ useSs: e.target.checked })}
          />
          Also publish to SS.ge
        </label>
      </div>

      {/* Filters */}
      <div className="mt-4">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-xs text-sky-300 hover:text-sky-200"
        >
          {expanded ? "▾ Hide filters" : "▸ Edit filters"}
        </button>
        {expanded && (
          <div className="mt-3 rounded-lg border border-white/5 bg-slate-950/40 p-3">
            <FilterBar
              filters={draft.filters}
              onChange={(f) => patch({ filters: f })}
            />
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          disabled={saving || !credsOk}
          onClick={save}
          className="rounded-lg bg-sky-500/90 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-500 disabled:opacity-40"
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          disabled={running || !row.rule || !credsOk}
          onClick={runNow}
          title={!row.rule ? "Save the rule first" : "Run this rule now"}
          className="rounded-lg bg-slate-700/70 px-3 py-1.5 text-sm text-slate-100 hover:bg-slate-700 disabled:opacity-40"
        >
          {running ? "Running…" : "Run now"}
        </button>
        <button
          type="button"
          onClick={onShowHistory}
          className="rounded-lg px-3 py-1.5 text-sm text-slate-400 hover:text-slate-200"
        >
          History
        </button>
        {msg && <span className="text-xs text-slate-400">{msg}</span>}
      </div>
    </div>
  );
}

function HistoryModal({
  row,
  onClose,
}: {
  row: AgentRow;
  onClose: () => void;
}) {
  const [runs, setRuns] = useState<RunOut[] | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [jobsByRun, setJobsByRun] = useState<
    Record<string, RunJobStatus[] | "loading">
  >({});
  const name = useMemo(() => row.full_name || row.email, [row]);

  useEffect(() => {
    void (async () => {
      try {
        const data = await api<RunOut[]>(
          `/api/automation/runs?agent_user_id=${row.agent_user_id}&limit=100`,
        );
        setRuns(data);
      } catch {
        setRuns([]);
      }
    })();
  }, [row.agent_user_id]);

  const toggleRun = async (r: RunOut) => {
    if (expanded === r.id) {
      setExpanded(null);
      return;
    }
    setExpanded(r.id);
    if (jobsByRun[r.id] === undefined && r.job_run_ids.length > 0) {
      setJobsByRun((m) => ({ ...m, [r.id]: "loading" }));
      try {
        const data = await api<RunJobStatus[]>(
          `/api/automation/runs/${r.id}/jobs`,
        );
        setJobsByRun((m) => ({ ...m, [r.id]: data }));
      } catch {
        setJobsByRun((m) => ({ ...m, [r.id]: [] }));
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[80vh] w-full max-w-2xl overflow-auto rounded-xl border border-white/10 bg-slate-900 p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-slate-100">
            Run history — {name}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200"
          >
            ✕
          </button>
        </div>
        {runs === null ? (
          <Skeleton className="h-40 w-full rounded-lg" />
        ) : runs.length === 0 ? (
          <p className="text-sm text-slate-500">No runs yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500">
              <tr className="text-left">
                <th className="py-1.5 pr-2" />
                <th className="py-1.5 pr-2">When</th>
                <th className="py-1.5 pr-2">Trigger</th>
                <th className="py-1.5 pr-2">Status</th>
                <th className="py-1.5">Result</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => {
                const hasJobs = r.job_run_ids.length > 0;
                const isOpen = expanded === r.id;
                const jobs = jobsByRun[r.id];
                return (
                  <Fragment key={r.id}>
                    <tr
                      className={`border-t border-white/5 ${
                        hasJobs ? "cursor-pointer hover:bg-white/5" : ""
                      }`}
                      onClick={hasJobs ? () => void toggleRun(r) : undefined}
                    >
                      <td className="py-1.5 pr-2 text-slate-500 w-4">
                        {hasJobs ? (isOpen ? "▾" : "▸") : ""}
                      </td>
                      <td className="py-1.5 pr-2 text-slate-300">
                        {fmtTime(r.triggered_at)}
                      </td>
                      <td className="py-1.5 pr-2 text-slate-400">
                        {r.trigger}
                      </td>
                      <td className="py-1.5 pr-2">
                        <StatusPill status={r.status} />
                      </td>
                      <td className="py-1.5 text-slate-300">{runSummary(r)}</td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-slate-950/40">
                        <td />
                        <td colSpan={4} className="py-2 pr-2">
                          {jobs === "loading" || jobs === undefined ? (
                            <span className="text-xs text-slate-500">
                              Loading jobs…
                            </span>
                          ) : jobs.length === 0 ? (
                            <span className="text-xs text-slate-500">
                              No jobs.
                            </span>
                          ) : (
                            <ul className="space-y-1">
                              {jobs.map((j) => (
                                <li
                                  key={j.job_run_id}
                                  className="flex items-center gap-2 text-xs"
                                >
                                  <StatusPill status={j.status} />
                                  <span className="text-slate-400 truncate max-w-[18rem]">
                                    {j.source_url || j.job_run_id}
                                  </span>
                                  {j.mh_url && (
                                    <a
                                      href={j.mh_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-sky-300 hover:text-sky-200"
                                    >
                                      MyHome
                                    </a>
                                  )}
                                  {j.ss_url && (
                                    <a
                                      href={j.ss_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-sky-300 hover:text-sky-200"
                                    >
                                      SS
                                    </a>
                                  )}
                                  {j.error_message && (
                                    <span className="text-rose-300 truncate max-w-[14rem]">
                                      {j.error_message}
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const cls =
    status === "done"
      ? "bg-emerald-500/15 text-emerald-300"
      : status === "error"
        ? "bg-rose-500/15 text-rose-300"
        : status === "running"
          ? "bg-sky-500/15 text-sky-300"
          : "bg-slate-500/15 text-slate-400";
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] ${cls}`}>
      {status}
    </span>
  );
}
